/**
 * PriceVatNotice — l'avís de preu sota un camp de preu d'estada (plans/preus-en-crear-reserves.md, F2).
 *
 * Només pinta: la decisió de si cal avisar és de `priceVatNotice` (`lib/pricing/price-vat-notice.ts`)
 * i qui l'usa li passa el resultat. Sense avís no pinta res. Mai bloqueja: ensenya els dos preus
 * bons més propers i, amb un toc, `onPick` rep el triat (en cèntims); ignorar-lo és no tocar res.
 */

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  PRICE_VAT_NOTICE_HINT,
  formatCents,
  priceVatNoticeText,
  type PriceVatNotice as PriceVatNoticeData,
} from '@/lib/pricing/price-vat-notice';

export interface PriceVatNoticeProps {
  /** El que torna `priceVatNotice`; `null` = res a dir. */
  notice: PriceVatNoticeData | null;
  /** El preu triat, en cèntims enters. */
  onPick: (cents: number) => void;
  disabled?: boolean;
  className?: string;
}

export function PriceVatNotice({ notice, onPick, disabled, className }: PriceVatNoticeProps) {
  if (!notice) return null;
  const options = [notice.belowCents, notice.aboveCents].filter((c): c is number => c !== null);

  return (
    <div
      className={cn('space-y-1.5 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900', className)}
      role="status"
      aria-live="polite"
      data-testid="price-vat-notice"
    >
      <p>{priceVatNoticeText(notice)}</p>
      {options.length > 0 && (
        <>
          <p>{PRICE_VAT_NOTICE_HINT}</p>
          <div className="flex flex-wrap gap-2">
            {options.map((cents) => (
              <Button
                key={cents}
                type="button"
                variant="outline"
                size="sm"
                className="bg-white text-amber-950"
                disabled={disabled}
                onClick={() => onPick(cents)}
              >
                {formatCents(cents)}
              </Button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default PriceVatNotice;
